/**
 * SB6-II-01 — Inspection Instructions list column definitions.
 *
 * Order locked per the story §Table:
 *   1) Instruction Code (BIGINT, left, sortable; default sort DESC)
 *   2) Project Name      (string, sortable)
 *   3) Site Name         (string, sortable)
 *   4) Asset Name        (string, sortable — BE-composed)
 *   5) Pilot             (read-only "{first} {last} ({tenant})")
 *   6) Assigned Users    (chips; click → popover; NOT sortable)
 *   7) Assigned Company  (click → popover when editable else text)
 *   8) Planned Date      (click → date popover, PM/TA only)
 *   9) Status            (enum chip)
 *  10) Edit              (pencil + "edit", width 96, NOT sortable)
 *
 * TNCY-9 reconciliation (AC-2) — the II list now surfaces TWO DISTINCT
 * cross-tenant signals, deliberately kept separate (no duplicate ownership
 * column):
 *
 *   1. The **ownership badge** (leading `<OwnershipBadge>` column, shared
 *      primitive): is this row the caller's OWN II, or a MEMBER row they can
 *      see cross-tenant? On a member row the badge names the OWNING tenant +
 *      the caller's role. This is the `ownership`/`ownerTenantName`/`role`
 *      metadata the BE added in projects/v1@2.6.0 (TNCY-8).
 *   2. The **Assigned Company** column (SB6-II-03 tenant chip,
 *      `assignedCompanyTenantId`/`assignedCompanyName`): who the II is
 *      assigned OUT to (the `assigned_to_tenant_id` path — XT-D1/D3).
 *
 * These answer different questions — "whose data is this?" vs "who is it
 * assigned to?" — so both render, but ownership is shown ONCE (the badge),
 * never duplicated into the Assigned Company column. On `scope=member` the BE
 * composes AND de-duplicates the assigned-out ∪ grant-exposed sets
 * server-side (a row that is both appears once); the FE renders that single
 * deduped list with one ownership badge (`useOwnerMemberIIs` merges own +
 * member, deduping defensively by `inspectionInstructionId`). The badge
 * column only appears when the merged list actually has member rows, so an
 * own-only tenant's table is unchanged.
 */
import { Box, Button, Link, Typography } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import type { GridColDef } from "@mui/x-data-grid";
import { Link as RouterLink } from "react-router-dom";
import { ownershipColumn } from "@aeriosiq/ownership";
import { InspectionInstructionStatusChip } from "./InspectionInstructionStatusChip.js";
import { AssignedUsersCell } from "./AssignedUsersCell.js";
import { AssignedCompanyCell } from "./AssignedCompanyCell.js";
import { PlannedDateCell } from "./PlannedDateCell.js";
import type { IiListItem } from "./types.js";
import { PencilIcon } from "@aeriosiq/ui/icons";

// SB6-II review fix — entity-link styling matches the Turbines/Sites pages
// (ParkCell/TypeCell): white text + a subtle underline (NOT the MUI Link blue),
// with a primary-coloured underline on hover. The underline alone signals "link".
//
// Colours resolve from the JS palette. The `var(--mui-palette-*)` form this
// used to carry emits nothing — the theme is built with plain `createTheme()`,
// not `extendTheme()` — so the underline fell back to `currentColor` and was
// indistinguishable from no underline at all (SB6-G2 Bug-7, fixed in
// `ParkCell` at the time and left standing here).
const iiLinkSx = (theme: Theme) => ({
  color: "text.primary",
  textUnderlineOffset: "3px",
  textDecorationColor: theme.palette.text.secondary,
  "&:hover": { textDecorationColor: theme.palette.primary.main },
});

export function buildInspectionInstructionColumns(args: {
  onEdit: (row: IiListItem) => void;
  /** TNCY-9 (AC-2) — prepend the cross-tenant ownership tell only when the
   *  merged list actually has member rows, so an own-only tenant's table is
   *  byte-for-byte the pre-TNCY-9 layout. Distinct from Assigned Company. */
  showOwnership?: boolean;
}): GridColDef<IiListItem>[] {
  const { onEdit, showOwnership = false } = args;
  return [
    ...(showOwnership
      ? [ownershipColumn<IiListItem>({ entityNoun: "inspection instruction" })]
      : []),
    {
      field: "instructionCode",
      headerName: "Instruction Code",
      sortable: true,
      flex: 0.7,
      minWidth: 130,
      align: "left",
      headerAlign: "left",
      renderCell: (p) => (
        <Box
          sx={{
            fontVariantNumeric: "tabular-nums",
            width: "100%",
            textAlign: "left",
          }}
        >
          {p.row.instructionCode}
        </Box>
      ),
    },
    {
      field: "projectName",
      headerName: "Project Name",
      sortable: true,
      flex: 1.1,
      minWidth: 150,
      // projectName is nullable since projects/v1@2.22.0 — a FIELD_CREATED
      // (orphan) II has no project until back-office links it. Show a muted
      // "Unlinked" placeholder rather than an empty cell (FCII decision C.7;
      // filterable via the "Link status" filter → linkage=unlinked).
      renderCell: (p) =>
        p.row.projectName ? (
          <Typography variant="body2" component="span">
            {p.row.projectName}
          </Typography>
        ) : (
          <Typography variant="body2" component="span" color="text.secondary">
            Unlinked
          </Typography>
        ),
    },
    {
      field: "siteName",
      headerName: "Site Name",
      sortable: true,
      flex: 0.9,
      minWidth: 130,
      valueGetter: (_v, row: IiListItem) => row.siteName ?? "",
      // SB6-II-03: Site name → link to the Site edit page (opens EditSiteDialog
      // via the ?site= deep-link). stopPropagation so it doesn't toggle row select.
      renderCell: (p) =>
        p.row.siteId && p.row.siteName ? (
          <Link
            component={RouterLink}
            to={`/sites?site=${p.row.siteId}`}
            variant="body2"
            underline="always"
            sx={iiLinkSx}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            {p.row.siteName}
          </Link>
        ) : (
          <Typography variant="body2" component="span">
            {p.row.siteName ?? "-"}
          </Typography>
        ),
    },
    {
      field: "assetName",
      headerName: "Asset Name",
      sortable: true,
      flex: 1,
      minWidth: 150,
      valueGetter: (_v, row: IiListItem) => row.assetName ?? "",
      // SB6-II-03: Asset name → link to the Turbine edit page (opens
      // EditTurbineDialog via the ?turbine= deep-link).
      renderCell: (p) =>
        p.row.assetId && p.row.assetName ? (
          <Link
            component={RouterLink}
            to={`/turbines?turbine=${p.row.assetId}`}
            variant="body2"
            underline="always"
            sx={iiLinkSx}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            {p.row.assetName}
          </Link>
        ) : (
          <Typography variant="body2" component="span">
            {p.row.assetName ?? "-"}
          </Typography>
        ),
    },
    {
      field: "pilotName",
      headerName: "Pilot",
      sortable: true,
      flex: 1,
      minWidth: 150,
      align: "left",
      headerAlign: "left",
      valueGetter: (_v, row: IiListItem) => row.pilotName ?? "",
      renderCell: (p) => (
        <Typography variant="body2" component="span">
          {p.row.pilotName ?? "-"}
        </Typography>
      ),
    },
    {
      field: "assignedUsers",
      headerName: "Assigned Users",
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      flex: 1.2,
      minWidth: 180,
      renderCell: (p) => <AssignedUsersCell row={p.row} />,
    },
    {
      field: "assignedCompany",
      headerName: "Assigned Company",
      sortable: true,
      flex: 1,
      minWidth: 160,
      align: "left",
      headerAlign: "left",
      valueGetter: (_v, row: IiListItem) => row.assignedCompanyName ?? "",
      renderCell: (p) => <AssignedCompanyCell row={p.row} />,
    },
    {
      field: "plannedDate",
      headerName: "Planned Date",
      sortable: true,
      flex: 0.8,
      minWidth: 140,
      valueGetter: (_v, row: IiListItem) => row.plannedDate ?? "",
      renderCell: (p) => <PlannedDateCell row={p.row} />,
    },
    {
      field: "state",
      headerName: "Status",
      sortable: true,
      flex: 0.8,
      minWidth: 150,
      renderCell: (p) => (
        <InspectionInstructionStatusChip state={p.row.state} />
      ),
    },
    {
      field: "__edit",
      headerName: "",
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      width: 96,
      align: "left",
      headerAlign: "left",
      renderCell: (p) => (
        <Button
          size="small"
          variant="text"
          startIcon={<PencilIcon fontSize="small" />}
          aria-label={`Edit inspection instruction ${String(
            p.row.instructionCode,
          )}`}
          onClick={(e) => {
            e.stopPropagation();
            onEdit(p.row);
          }}
        >
          Edit
        </Button>
      ),
    },
  ];
}
